import React, { useRef } from 'react';
import parse from 'html-react-parser';
import { useDomainTestScoreQuery } from 'components/dashboard/quiz/domain/DomainQuizSlice';
import { useNewAptitudeTestScoreQuery } from 'components/dashboard/quiz/newAptitude/NewAptitudeQuizSlice';
import Loading from 'components/global/layout/Loading';
import QuizScoreChart from 'components/dashboard/quiz/layout/QuizScoreChart';

import { PDFExport } from '@progress/kendo-react-pdf';

interface StudentReportProps {
  enrollmentDate: string;
  projectData?: any;
  userData?: any;
  feedbackUnlockDate?: string | null;
  enrollData?: any;
}

const newTerms = {
  COMMUNICATION: 'Interpersonal Communication',
  COLLABORATION: 'Collaborative Engagement',
  MINDSET: 'Analytical Mindset',
  MANAGEMENT: 'Self Management',
};

interface ScoreComponent {
  category: 'COMMUNICATION' | 'COLLABORATION' | 'MINDSET' | 'MANAGEMENT';
  percentage: number;
}

const StudentReport = ({
  enrollmentDate,
  projectData,
  userData,
  feedbackUnlockDate,
  enrollData,
}: StudentReportProps) => {
  // Get domainId for fetching related domain scores
  const domainId = projectData?.domainId?._id;

  // Fetch domain test score data
  const {
    data: domainTestData,
    isLoading: isDomainTestLoading,
    isSuccess: isDomainTestSuccess,
    isError: isDomainTestError,
  } = useDomainTestScoreQuery();

  // Fetch Aptitude test score data
  const {
    data: aptitudeData,
    isLoading: isAptitudeLoading,
    isSuccess: isAptitudeSuccess,
    isError: isAptitudeError,
  } = useNewAptitudeTestScoreQuery();

  // Find the specific domain data relevant to this project
  const currentDomainData = React.useMemo(() => {
    if (isDomainTestSuccess && domainTestData && domainId) {
      return domainTestData.find((d: any) => d.domain._id === domainId);
    }
    return null;
  }, [domainTestData, isDomainTestSuccess, domainId]);

  // Helper function to calculate domain skill percentage
  function getSkillPercentage(skillId: any) {
    if (!currentDomainData || !currentDomainData.finalScore) return 0;
    const currentSkill = currentDomainData.finalScore.find(
      (skill: any) => skill.skillId === skillId
    );
    if (!currentSkill || !currentSkill.max) return 0;
    return Math.ceil((currentSkill.obtained * 100) / currentSkill.max);
  }

  // Helper function to join skill titles into a comma-separated string
  const getSkillsDescription = () => {
    return projectData?.skillDescription || 'Not available';
  };

  const getPerformanceDetails = (score: number) => {
    if (score >= 40 && score < 50) return { level: '📈 Leveling Up', evaluation: 'Understanding the basics, but improvement is required.' };
    if (score >= 50 && score < 70) return { level: '🏅 Skilled', evaluation: 'Good grasp of concepts, but can refine further.' };
    if (score >= 70 && score < 90) return { level: '🚀 Expert Mode', evaluation: 'Strong execution with minor areas for optimization.' };
    if (score >= 90) return { level: '🏆 Mastermind', evaluation: 'Outstanding performance, ready for industry applications!' };
    return { level: 'N/A', evaluation: 'No evaluation available.' };
  };

  const pdfExportComponent = useRef<PDFExport>(null);

  const generatePdf = () => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save();
    }
  };

  return (
    <div className='p-6 max-w-4xl mx-auto'>
      <PDFExport
        ref={pdfExportComponent}
        paperSize='A4'
        fileName={`${userData?.firstName || 'student'}-${projectData?.title || 'capstone'}-report.pdf`}
        author={userData?.firstName || 'Belong'}
        subject='Capstone Report'
        title={`${projectData?.title || 'Capstone'} Report`}
        scale={0.8}
        margin='1cm'>
        <div className='pdf-content'>
          <h2 className='text-2xl font-bold mb-6 text-blue-700 border-b pb-2'>
            Project Completion Report
          </h2>

          <div className='grid grid-cols-1 gap-6 mb-6'>
            <div className='bg-gray-50 p-4 rounded-lg shadow-sm'>
              <div className='space-y-2'>
                <p><strong>Name:</strong> {userData?.firstName} {userData?.lastName || ''}</p>
                <p><strong>Program Duration:</strong> {enrollmentDate} - {projectData?.completionDate || 'In Progress'}</p>
                <p><strong>Project Title:</strong> {projectData?.title || 'Not available'}</p>
                <p><strong>Domain:</strong> {projectData?.domainId?.title || 'Not available'}</p>
                
                <p className="mt-4">
                  You have worked on real-world industry projects, focusing on
                  self-upskilling and gaining hands-on experience in applying
                  technical skills to real-world challenges. This report
                  provides a personalized summary of your journey, highlighting
                  your project work, skill development, feedback, and
                  recommended next steps.
                </p>
                
                <div className='mt-6 mb-6'>
                  <h3 className='text-lg font-semibold mb-3 text-blue-600'>Project Overview</h3>
                  <div className='prose max-w-none'>{parse(projectData?.projectOverview || projectData?.description || 'Not available')}</div>
                </div>
                
                <div className='mb-6'>
                  <h3 className='text-lg font-semibold mb-3 text-blue-600'>Skill Development</h3>
                  <div className='mb-2 prose max-w-none'>{parse(getSkillsDescription())}</div>
                </div>
              </div>

              <div className='mb-6 mt-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>Capstone Evaluation</h3>
                <table className='w-full border-2 border-black'>
                  <thead>
                    <tr>
                      <th className='text-left p-1 border-2 border-black'>Task</th>
                      <th className='text-left p-1 border-2 border-black'>Quiz Score (%)</th>
                      <th className='text-left p-1 border-2 border-black'>Evaluation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectData?.tasks?.map((task: any, index: number) => {
                      const quizAttempt = enrollData?.quiz?.find((q: any) => q.taskNo === task.taskNo);
                      let scoreDisplay: string | number = 0;
                      let scoreValue: number = 0;
                      if (quizAttempt) {
                        scoreValue = quizAttempt.percentage ?? 0;
                        scoreDisplay = scoreValue === 0 ? '0' : scoreValue;
                      }
                      const { evaluation } = getPerformanceDetails(scoreValue);
                      return (
                        <tr key={index}>
                          <td className='border-2 p-1 border-black'>{task.title || 'Not available'}</td>
                          <td className='border-2 p-1 border-black'>{scoreDisplay}</td>
                          <td className='border-2 p-1 border-black'>{evaluation}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='mb-6 mt-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>Aptitude Quiz Score</h3>
                {isAptitudeLoading && <Loading />}
                {isAptitudeError && <div>Error loading aptitude score.</div>}
                {isAptitudeSuccess && !aptitudeData && <div>No aptitude score found.</div>}
                {isAptitudeSuccess && aptitudeData && (
                  <div className='bg-gray-100 p-4 mt-4 rounded'>
                    <div className='text-gray-700 font-semibold'>
                      <div>
                        <div className='mb-2 text-lg'>Overall Aptitude Score</div>
                        <div className='text-2xl font-bold text-primary mb-4'>
                          {Math.ceil(aptitudeData.percentage)}%
                        </div>
                      </div>
                      <div className='mt-6 w-full'>
                        <div className='mb-2 text-md font-semibold'>Workplace Readiness Skills Assessed:</div>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                          {aptitudeData.finalScore.map((comp: ScoreComponent) => (
                            <div
                              key={newTerms[comp.category]}
                              className='my-1 flex items-center bg-primary-lightest rounded-3xl p-2'>
                              <div className='bg-primary rounded-full text-center flex items-center justify-center w-12 h-12 text-white font-semibold text-sm flex-shrink-0'>
                                {Math.ceil(comp.percentage)}%
                              </div>
                              <div className='ml-3 text-sm flex-grow'>
                                {newTerms[comp.category]}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className='mb-6 mt-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>Domain Quiz Score</h3>
                {isDomainTestLoading && <Loading />}
                {isDomainTestError && <div>Error loading domain scores.</div>}
                {isDomainTestSuccess && !currentDomainData && <div>No domain score found for this project's domain.</div>}
                {isDomainTestSuccess && currentDomainData && (
                  <div className='bg-gray-100 p-4 mt-4 rounded'>
                    <div className='text-gray-700 font-semibold'>
                       <div>
                        <div className='mb-2 text-lg'>Score for: {currentDomainData.domain.title}</div>
                        <div className='text-2xl font-bold text-primary mb-4'>
                          {currentDomainData.percentage}%
                        </div>
                      </div>
                      <div className='mt-6 w-full'>
                        <div className='mb-2 text-md font-semibold'>Skills Assessed:</div>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                          {currentDomainData.skills.map((skill: any) => {
                            const skillPercentage = getSkillPercentage(skill._id);
                            return (
                              <div key={skill._id} className='my-1 flex items-center bg-primary-lightest rounded-3xl p-2'>
                                <div className='bg-primary rounded-full text-center flex items-center justify-center w-12 h-12 text-white font-semibold text-sm flex-shrink-0'>
                                  {skillPercentage}%
                                </div>
                                <div className='ml-3 text-sm flex-grow'>{skill.title}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className='mb-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>Recommendations & Next Steps in Skill-Building</h3>
                <div className='prose max-w-none'>{parse(projectData?.nextSteps || projectData?.description || 'Not available')}</div>
              </div>
              
              <div className='mb-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>Industry Capstone: Building the Foundation for Your Future in Tech!</h3>
                <p>
                  Congratulations on successfully completing the Industry Super
                  Capstone! You've navigated real-world challenges, refined your
                  problem-solving and analytical skills, and gained hands-on
                  experience with industry-standard tools and methodologies.
                  This project wasn't just about theoretical knowledge—it was
                  about applying advanced technical concepts, leveraging
                  data-driven insights, and demonstrating your ability to drive
                  impactful solutions. Keep the momentum going: continue to
                  innovate, embrace new technologies, and leverage this
                  experience as a springboard for advanced career opportunities
                  in your field.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PDFExport>

      <div className='mt-6 flex justify-end pdf-button-container'>
        <button
          onClick={generatePdf}
          className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors'>
          Download as PDF
        </button>
      </div>
    </div>
  );
};

export default StudentReport;
